import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';

import useFightClock, { formatClock } from 'hooks/useFightClock';

const useStyles = makeStyles(theme => ({
    root: { display: 'inline-flex', alignItems: 'baseline', gap: theme.spacing(1) },
    // The state ABOVE the time (26 Oct 2026: «статус… над часом, буде так
    // краще видно») — the mat's fight card.
    rootAbove: { flexDirection: 'column-reverse', alignItems: 'center', gap: 2, '& $state': { fontSize: 14, fontWeight: 700 } },
    time: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 800,
        lineHeight: 1,
        // Digits must not shuffle sideways as they change — this is read at a
        // glance, from a distance, while something else is happening.
        fontFamily: 'monospace',
    },
    running: { color: theme.palette.success.main },
    paused: { color: theme.palette.warning.main },
    stopped: { color: theme.palette.text.secondary },
    expired: { color: theme.palette.error.main },
    state: {
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: theme.palette.text.secondary,
    },
}));

/**
 * THE CLOCK, as a reader sees it.
 *
 * Purely presentational: it renders the server's clock through
 * {@see useFightClock} and owns no time of its own. Both the secretary's
 * workspace and the public display use this one component, so a mat and its
 * hall screen cannot show different times — there is a single rendering of
 * "how long has this fight run".
 *
 * With a configured bout duration it counts DOWN to the limit and turns red at
 * zero; without one it counts up. It never invents a limit the ruleset does
 * not set.
 */
/** What the clock is doing, in a word: run out beats running, paused or stopped. */
const clockLabel = clock => {
    if (clock.expired) {
        return 'EXPIRED';
    }

    return 'STOPPED' === clock.state && 0 === clock.elapsedMs ? 'IDLE' : clock.state;
};

const FightClock = props => {
    const { timer, size, showState, stateAbove } = props;
    const classes = useStyles();
    const [t] = useTranslation();
    const clock = useFightClock(timer);

    if (!timer) {
        return null;
    }

    let tone = classes.stopped;
    if (clock.expired) {
        tone = classes.expired;
    } else if (clock.running) {
        tone = classes.running;
    } else if (clock.state === 'PAUSED') {
        tone = classes.paused;
    }

    return (
        <span
            className={`${classes.root} ${stateAbove ? classes.rootAbove : ''}`}
            data-testid='fight-clock'
            data-state={clock.state}
            data-expired={clock.expired ? 'true' : 'false'}
        >
            <span className={`${classes.time} ${tone}`} style={{ fontSize: size }}>
                {formatClock(clock.displayMs)}
            </span>
            {showState && (
                <span className={classes.state}>
                    {/* A stopped clock that never ran has not «finished». */}
                    {/* Time run out says so — not «Триває» or «Пауза» (26 Oct 2026). */}
                    {t(`operations.timer.state.${clockLabel(clock)}`)}
                </span>
            )}
        </span>
    );
};

FightClock.propTypes = {
    /** The server's clock payload; null when the protocol has no timer. */
    timer: PropTypes.object,
    /** Font size in px — a wall display needs a far bigger one than a laptop. */
    size: PropTypes.number,
    showState: PropTypes.bool,
    /** The state written above the time, not beside it. */
    stateAbove: PropTypes.bool,
};

FightClock.defaultProps = {
    timer: null,
    size: 34,
    showState: true,
    stateAbove: false,
};

export default FightClock;
